import { JSX, ReactElement } from 'react';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { Datagrid, DateField, Identifier, List, TextField, WithRecord } from 'react-admin';
import { AdminMiniDescriptor } from '../admin-mini.types';
import { AdminMiniAccess } from '../hooks/admin-mini-access';

export interface AdminMiniRunListProps {
    tool: AdminMiniDescriptor;
    /** Only this admin's own runs. */
    adminUserId?: Identifier;
    /** Which run's results are open, so the list can mark it. */
    selectedId?: Identifier;
    onShowResults: (run: any) => void;
    onCancel: (id: Identifier) => void;
    cancelling: boolean;
    access: AdminMiniAccess;
}

/** Runs an admin can still stop. Matches `Cancel`'s own view on the API side. */
const CANCELLABLE: string[] = ['draft', 'queued', 'running', 'scoring'];

/**
 * How each status is coloured. `scoring` is deliberately the same tone as `running`: from the
 * admin's point of view the run is still working, and a separate colour would suggest a
 * distinction they can act on.
 */
const STATUS_COLOURS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
    draft: 'default',
    queued: 'info',
    running: 'info',
    scoring: 'info',
    complete: 'success',
    cancelled: 'warning',
    archive: 'default',
};

/**
 * The runs an admin has started with one tool.
 *
 * Shared between Mystery Shopper and Compliance Sniffer, so the two lists cannot drift into
 * showing different things about the same kind of record. What differs is the descriptor: the
 * resource it reads and what a run is called.
 *
 * **Filtered to the signed-in admin.** Without it an admin sees every other admin's runs and a
 * Stop button beside each one.
 */
export const AdminMiniRunList: (props: AdminMiniRunListProps) => JSX.Element =
    (props: AdminMiniRunListProps): JSX.Element => {
        const { tool, access } = props;

        return (
            <List actions={ false }
                  resource={ tool.runResource }
                  perPage={ 10 }
                  filter={ { 'adminUser.id': props.adminUserId } }
                  sort={ { field: 'id', order: 'DESC' } }
                  empty={ <EmptyRunList runNoun={ tool.runNoun } /> }
                  title=" ">
                <Datagrid bulkActionButtons={ false }
                          rowClick={ false }
                          rowSx={ (record: any): object => ({
                              // The run whose results are showing beside the list.
                              background: String(record?.id) === String(props.selectedId ?? '')
                                  ? 'action.hover'
                                  : 'transparent',
                          }) }>
                    <TextField source="name" />

                    <WithRecord label="Status" render={ (record: any): ReactElement => (
                        <Chip size="small"
                              variant="outlined"
                              color={ STATUS_COLOURS[record?.status] ?? 'default' }
                              label={ record?.status ?? 'unknown' }
                              sx={ { textTransform: 'capitalize' } } />
                    ) } />

                    <WithRecord label="Recipients" render={ (record: any): ReactElement => (
                        <span>{ (record?.adminTargets ?? []).length }</span>
                    ) } />

                    <WithRecord label="Scored against" render={ (record: any): ReactElement => {
                        const noks: any[] = record?.adminConfiguration?.noks ?? [];

                        // A run with no Noks is never graded. Before 2026-09-11 that was every
                        // admin run, and nothing on any screen said so.
                        if (noks.length === 0) {
                            return (
                                <Tooltip title="Nothing will score the replies to this run">
                                    <Chip size="small" color="warning" variant="outlined" label="No Noks" />
                                </Tooltip>
                            );
                        }

                        return (
                            <Tooltip title={ noks.map((nok: any): string => String(nok?.name ?? '')).join(', ') }>
                                <span>{ `${ noks.length } ${ noks.length === 1 ? 'Nok' : 'Noks' }` }</span>
                            </Tooltip>
                        );
                    } } />

                    <DateField source="createdAt" showTime />

                    <WithRecord label="Actions" render={ (record: any): ReactElement => (
                        <Box sx={ { display: 'flex', gap: '8px' } }>
                            <Button size="small" onClick={ (): void => props.onShowResults(record) }>
                                { 'Results' }
                            </Button>
                            { access.can('cancel') && CANCELLABLE.includes(record?.status) && (
                                <Button size="small"
                                        color="warning"
                                        disabled={ props.cancelling }
                                        onClick={ (): void => props.onCancel(record.id) }>
                                    { 'Stop' }
                                </Button>
                            ) }
                        </Box>
                    ) } />
                </Datagrid>
            </List>
        );
    };

const EmptyRunList: (props: { runNoun: string }) => JSX.Element =
    (props: { runNoun: string }): JSX.Element => (
        <Box sx={ { width: '100%', padding: '48px 24px', textAlign: 'center', color: 'text.secondary' } }>
            <Typography sx={ { fontSize: '14px' } }>
                { `No ${ props.runNoun }s yet. Start one and it will appear here.` }
            </Typography>
        </Box>
    );
